import {
  DataTable,
  FAIcon,
  HideInProduction,
  HoverColorPicker,
  OnlyInProduction,
} from '@/components'
import type { AbsenceTypeNode } from '@/modules/timesheets/types.graphql'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useCallback, useMemo } from 'react'
import { AbsenceTypeDialog } from './absence-type-dialog'
import type { AbsenceTypesListProps } from './types'

interface AbsenceSettingsTableProps extends AbsenceTypesListProps {
  temporaryEditColor: {
    [id: string]: string | null
  }
  onUpdateColor: (categoryId: string) => (color: string) => void
}

export const AbsenceSettingsTable: React.FC<AbsenceSettingsTableProps> = ({
  absenceTypes,
  temporaryEditColor,

  onEditSubmit,
  onDelete,
  onUpdateColor,
}) => {
  const translations = useTranslate({
    name: 'common.name',
    preset: 'settings.absence.absence-type.preset',

    add: 'common.add',
    edit: 'common.edit',
    delete: 'common.delete-alt',
    cancel: 'common.cancel',
    id: 'common.id',

    numTypes: ['settings.absence.absence-types.num-types', { n: 0 }],

    presetType: {
      SIMPLE: 'settings.absence.absence-type.presets.simple.title',
      VACATION_NO: 'settings.absence.absence-type.presets.vacation-no.title',
      SELF_CERTIFICATION_NO:
        'settings.absence.absence-type.presets.self-certification-no.title',
      SICK_CHILD_NO:
        'settings.absence.absence-type.presets.sick-child-no.title',
      FLEXTIME: 'settings.absence.absence-type.presets.flextime.title',
    },
  })

  const handleEditClick = useCallback(
    (type: AbsenceTypeNode) => {
      onEditSubmit(type)
    },
    [onEditSubmit]
  )

  const column = createColumnHelper<AbsenceTypeNode>()

  const tableColumns = useMemo(
    () => [
      column.display({
        id: 'color',
        meta: {
          narrow: true,
        },
        cell(info) {
          const { id, color } = info.row.original

          return (
            <HoverColorPicker
              value={temporaryEditColor[id] ?? color}
              onChange={onUpdateColor(id)}
            />
          )
        },
      }),
      column.accessor('externalId', {
        header: translations.id,
        enableSorting: true,
        meta: {
          narrow: true,
          center: true,
        },
      }),
      column.accessor('name', {
        header: translations.name,
        enableSorting: true,
        meta: {
          narrow: true,
        },
      }),
      column.accessor('preset', {
        header: translations.preset,
        cell(info) {
          const preset = info.getValue()

          return <span>{!preset ? '-' : translations.presetType[preset]}</span>
        },
      }),
      column.display({
        id: 'edit',
        meta: {
          narrow: true,
        },
        cell(info) {
          const type = info.row.original

          return (
            <>
              <HideInProduction>
                <AbsenceTypeDialog absenceType={type} />
              </HideInProduction>

              <OnlyInProduction>
                <FAIcon
                  className="text-gray-400 hover:text-red-500"
                  icon="edit"
                  cursor="pointer"
                  onClick={() => handleEditClick(type)}
                />
              </OnlyInProduction>
            </>
          )
        },
      }),
      column.display({
        id: 'delete',
        meta: {
          narrow: true,
        },
        cell(info) {
          const type = info.row.original

          return (
            <FAIcon
              className="text-gray-400 hover:text-red-500"
              icon="times"
              cursor="pointer"
              onClick={() => onDelete(type)}
            />
          )
        },
      }),
    ],
    [
      column,
      handleEditClick,
      onDelete,
      onUpdateColor,
      temporaryEditColor,
      translations.id,
      translations.name,
      translations.preset,
      translations.presetType,
    ]
  )

  const tableData = useMemo(() => absenceTypes, [absenceTypes])

  return (
    <DataTable
      columns={tableColumns}
      data={tableData}
      pagination={{
        itemsText: n => translations.numTypes({ n }),
      }}
    />
  )
}
